// src/modules/app-views/_apps/appsindico/units/pages/Document/tabs/DependentsTab.jsx
// *******************************************************************************
// DEPENDENTS TAB
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useRef } from 'react';
import { Row, Col, Table, Button, Flex, Spin, Popconfirm, Modal, Form, Select } from 'antd';
import { PlusOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { useDocumentContext } from 'components/core/DocumentProvider';
import axiosService from 'services/axiosService';
import appService from 'services/appService';
import translateX from 'utils/translateX';
import { API } from 'constants/Api';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const DEBOUNCE_DELAY = 400;
const MIN_SEARCH_LENGTH = 2;

const KINSHIP_OPTIONS = [
  'husband_wife',
  'father_mother',
  'son_daughter',
  'brother_sister',
  'grandfather_mother',
  'grandson_daughter',
  'nephew_niece',
  'uncle_aunt',
  'cousin',
  'boyfriend_girlfriend',
  'fiance_fiancee',
  'friend',
  'stepfather_mother',
  'stepson_daughter',
  'other',
].map(k => ({ value: k, label: translateX(k) }));

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const DependentsTab = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { data, _id, refresh } = useDocumentContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const [form] = Form.useForm();
  const debounceTimerRef = useRef(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [entityOptions, setEntityOptions] = useState([]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleSearch = useCallback(searchValue => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    if (!searchValue || searchValue.length < MIN_SEARCH_LENGTH) {
      setEntityOptions([]);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);

    debounceTimerRef.current = setTimeout(async () => {
      try {
        const [entities] = await axiosService.post({
          url: API.ENTITIES.POWER_SEARCH,
          data: { search: searchValue },
          returnAsObject: false,
        });

        const list = Array.isArray(entities) ? entities : [];
        const opts = list
          .filter(e => !e.finalConsumer)
          .slice(0, 10)
          .map(e => ({
            value: e._id,
            label: e.name || e.fullName || e.razaoSocial || '-',
          }));

        setEntityOptions(opts);
      } catch {
        setEntityOptions([]);
      } finally {
        setSearchLoading(false);
      }
    }, DEBOUNCE_DELAY);
  }, []);

  const handleAdd = useCallback(() => {
    form.resetFields();
    setEntityOptions([]);
    setShowModal(true);
  }, [form]);

  const handleSave = useCallback(async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const currentDependents = data?.dependents || [];
      const newDependents = [
        ...currentDependents,
        {
          entityId: values.entityId,
          kinship: values.kinship || 'other',
        },
      ];

      await axiosService.put({
        url: `/v1/units/${_id}`,
        data: { dependents: newDependents },
      });

      refresh?.();
      setShowModal(false);
      appService.message({ flag: 's', text: 'saved_successfully' });
    } catch (error) {
      if (!error?.errorFields) {
        appService.message({ flag: 'e', text: 'error_saving' });
      }
    } finally {
      setLoading(false);
    }
  }, [form, data?.dependents, _id, refresh]);

  const handleRemove = useCallback(
    async index => {
      setLoading(true);
      try {
        const currentDependents = data?.dependents || [];
        const newDependents = currentDependents.filter((_, i) => i !== index);

        await axiosService.put({
          url: `/v1/units/${_id}`,
          data: { dependents: newDependents },
        });

        refresh?.();
        appService.message({ flag: 's', text: 'removed_successfully' });
      } catch {
        appService.message({ flag: 'e', text: 'error_removing' });
      } finally {
        setLoading(false);
      }
    },
    [data?.dependents, _id, refresh]
  );

  const handleModalCancel = useCallback(() => {
    setShowModal(false);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const dependents = data?.dependents || [];

  const columns = [
    {
      title: translateX('name'),
      dataIndex: ['__entity', 'name'],
      render: (text, record) => record?.__entity?.name || record?.entityId || '-',
    },
    {
      title: translateX('kinship'),
      dataIndex: ['kinship'],
      width: 180,
      render: text => translateX(text) || '-',
    },
    {
      title: translateX('actions'),
      width: 80,
      align: 'center',
      render: (_, __, index) => (
        <Popconfirm title={translateX('confirm_delete')} onConfirm={() => handleRemove(index)}>
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            disabled={loading}
          />
        </Popconfirm>
      ),
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <Table
        rowKey="_id"
        columns={columns}
        dataSource={dependents}
        pagination={false}
        loading={loading}
        size="small"
      />

      <Flex justify="end" className="mt-3">
        <Button icon={<PlusOutlined />} onClick={handleAdd} disabled={loading}>
          {translateX('add_dependent')}
        </Button>
      </Flex>

      <Modal
        title={translateX('add_dependent')}
        open={showModal}
        onCancel={handleModalCancel}
        onOk={handleSave}
        confirmLoading={loading}
        destroyOnHidden
        width={480}
      >
        <Form form={form} layout="vertical" className="mt-3">
          <Form.Item
            name="entityId"
            label={translateX('person')}
            rules={[{ required: true, message: () => translateX('required_field') }]}
          >
            <Select
              showSearch
              allowClear
              filterOption={false}
              placeholder={translateX('search_entity')}
              onSearch={handleSearch}
              notFoundContent={
                searchLoading ? (
                  <Flex justify="center" style={{ padding: 8 }}>
                    <Spin size="small" />
                  </Flex>
                ) : null
              }
              suffixIcon={searchLoading ? <Spin size="small" /> : <SearchOutlined />}
              options={entityOptions}
              style={{ height: 40 }}
            />
          </Form.Item>
          <Form.Item name="kinship" label={translateX('kinship')}>
            <Select
              options={KINSHIP_OPTIONS}
              placeholder={translateX('select')}
              style={{ height: 40 }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default DependentsTab;
